import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState } from '@reduxjs/toolkit';

import { AppState } from '.';
import { Account, BillingInformation, exceptionOf, Operator } from '../models';
import { BillingInformationService } from '../services';

export const BILLING_INFORMATION_FEATURE_KEY = 'billingInformation';

interface BillingRequest {
  status: 'idle' | 'pending' | 'fulfilled' | 'empty' | 'error';
  requestId?: string;
  startedAt?: number;
  saving?: boolean;
}

interface BillingInformationState extends EntityState<BillingInformation> {
  loading: boolean;
  requestsByAccount: Record<number, BillingRequest>;
  isEditBillingInfo: boolean;
  isValid: boolean;
  error?: string;
}

const billingInformationAdapter = createEntityAdapter<BillingInformation>();

export const createInitialState = (partialState: Partial<BillingInformationState> = {}) => (
  billingInformationAdapter.getInitialState({
    loading: false,
    requestsByAccount: {},
    isEditBillingInfo: false,
    isValid: false,
    error: undefined,
    ...partialState,
  })
);

export const doCreateBillingInformation = createAsyncThunk(
  'billingInformation/createBillingInformation',
  async (input: Parameters<typeof BillingInformationService['upsertBillingInformation']>[0], { rejectWithValue }) => {
    try {
      const billingContact = await BillingInformationService.upsertBillingInformation(input);
      return billingContact;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

// A GET still unsettled after this long no longer blocks a new one; its late result is dropped
// by the requestId check in the reducers. Axios has no timeout, so this bounds a hung request's
// dedup slot.
export const BILLING_GET_STALE_MS = 30_000;

type GetMeta = { pendingMeta: { startedAt: number } };
export const doGetBillingInformation = createAsyncThunk<BillingInformation[], number, GetMeta>(
  'billingInformation/getBillingInformation',
  async (accountId: BillingInformation['accountId']) => {
    const billingContact = await BillingInformationService.getBillingInformation({
      field: 'accountId',
      operator: Operator.EQ,
      value: accountId.toString(),
    });

    if (billingContact.length === 0) {
      const error = new Error('Billing information is empty');
      error.name = 'BillingInformationEmpty';
      throw error;
    }

    return billingContact;
  },
  {
    condition: (accountId, { getState }) => {
      const request = (getState() as AppState).billingInformation.requestsByAccount[accountId];
      return !request?.requestId || Date.now() - (request.startedAt ?? 0) >= BILLING_GET_STALE_MS;
    },
    getPendingMeta: () => ({ startedAt: Date.now() }),
  },
);

const replaceAccountRows = (
  state: BillingInformationState,
  accountId: number,
  rows: BillingInformation[],
) => {
  const ids = state.ids.filter(id => state.entities[id]?.accountId === accountId);
  billingInformationAdapter.removeMany(state, ids);
  billingInformationAdapter.upsertMany(state, rows.filter(row => row.accountId === accountId));
};

const updateLoading = (state: BillingInformationState) => {
  state.loading = Object.values(state.requestsByAccount).some(request => request.status === 'pending' || request.saving);
};

const billingInformationSlice = createSlice({
  name: BILLING_INFORMATION_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {
    setBillingInfoValid: (state, action) => {
      state.isValid = action.payload;
    },
    setEditBillingInfo: (state, action) => {
      state.isEditBillingInfo = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(doCreateBillingInformation.pending, (state, action) => {
      const { accountId } = action.meta.arg;
      state.requestsByAccount[accountId] = {
        ...state.requestsByAccount[accountId],
        status: state.requestsByAccount[accountId]?.status ?? 'idle',
        saving: true,
      };
      updateLoading(state);
    });
    builder.addCase(doCreateBillingInformation.fulfilled, (state, action) => {
      const { accountId } = action.meta.arg;
      replaceAccountRows(state, accountId, [action.payload]);
      // Keep the physical GET slot for dedup, but only pending results may commit.
      // A GET started before this save can now settle without changing the saved row.
      state.requestsByAccount[accountId] = {
        ...state.requestsByAccount[accountId], status: 'fulfilled', saving: false,
      };
      state.error = '';
      updateLoading(state);
    });
    builder.addCase(doCreateBillingInformation.rejected, (state, action) => {
      const { accountId } = action.meta.arg;
      state.requestsByAccount[accountId] = {
        ...state.requestsByAccount[accountId],
        status: state.requestsByAccount[accountId]?.status ?? 'idle',
        saving: false,
      };
      state.error = action.error.message;
      updateLoading(state);
    });
    builder.addCase(doGetBillingInformation.pending, (state, action) => {
      state.requestsByAccount[action.meta.arg] = {
        ...state.requestsByAccount[action.meta.arg],
        status: 'pending',
        requestId: action.meta.requestId,
        startedAt: action.meta.startedAt,
      };
      state.error = '';
      updateLoading(state);
    });
    builder.addCase(doGetBillingInformation.fulfilled, (state, action) => {
      const request = state.requestsByAccount[action.meta.arg];
      if (request?.requestId !== action.meta.requestId) return;
      delete request.requestId;
      if (request.status === 'pending') {
        replaceAccountRows(state, action.meta.arg, action.payload);
        request.status = 'fulfilled';
      }
      updateLoading(state);
    });
    builder.addCase(doGetBillingInformation.rejected, (state, action) => {
      const request = state.requestsByAccount[action.meta.arg];
      if (request?.requestId !== action.meta.requestId) return;
      delete request.requestId;
      if (request.status === 'pending') {
        const empty = action.error.name === 'BillingInformationEmpty';
        if (empty) replaceAccountRows(state, action.meta.arg, []);
        request.status = empty ? 'empty' : 'error';
        state.error = empty ? '' : action.error.message;
      }
      updateLoading(state);
    });
  },
});

const selectBillingInformationFeature = (state: AppState) => state[
  BILLING_INFORMATION_FEATURE_KEY
];
const { selectAll } = billingInformationAdapter.getSelectors(selectBillingInformationFeature);
export const selectOneByAccountId = (accountId: Account['id']) => createSelector(
  selectAll,
  billingInformations => billingInformations.find(it => it.accountId === accountId),
);
export const selectLoading = createSelector(
  selectBillingInformationFeature,
  state => state.loading,
);

export const selectStatusByAccountId = (accountId: Account['id']) => createSelector(
  selectBillingInformationFeature,
  state => state.requestsByAccount[accountId]?.status ?? 'idle',
);

export const selectLoadingByAccountId = (accountId: Account['id']) => createSelector(
  selectBillingInformationFeature,
  state => state.requestsByAccount[accountId]?.status === 'pending'
    || !!state.requestsByAccount[accountId]?.saving,
);

export const isNotSyncedAddressByAccountId = (accountId: Account['id']) => createSelector(
  selectAll,
  billingInformations => {
    const billingInfo = billingInformations.find(it => it.accountId === accountId);
    return billingInfo && !billingInfo.synced;
  },
);

export const isBillingInfoValid = createSelector(
  selectBillingInformationFeature,
  state => state.isValid,
);

export const isEditBillingInfo = createSelector(
  selectBillingInformationFeature,
  state => state.isEditBillingInfo,
);

export const {
  setBillingInfoValid,
  setEditBillingInfo,
} = billingInformationSlice.actions;
export default billingInformationSlice.reducer;
